import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Contato() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    servico: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    // Update page title and meta description for SEO
    document.title = 'Contato - N&N Soluções Empresariais | Fale Conosco';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Entre em contato com a N&N Soluções Empresariais. WhatsApp (62) 99391-1090, email contato@nnsolucoes.com.br. Atendimento de segunda a sexta, 8h às 18h.');
    }

    // Add structured data for contact page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contato N&N Soluções Empresariais",
      "description": "Entre em contato conosco para serviços de contabilidade e consultoria",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contato`,
      "mainEntity": {
        "@type": "Organization",
        "name": "N&N Soluções Empresariais",
        "telephone": "+5562993911090",
        "email": "contato@nnsolucoes.com.br",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Av. Goiás, 606",
          "addressLocality": "Goiânia",
          "addressRegion": "GO",
          "postalCode": "74015-010",
          "addressCountry": "BR"
        },
        "openingHours": "Mo-Fr 08:00-18:00"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.text.includes('ContactPage')) {
          script.remove();
        }
      });
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3orgs4uve6meee39log', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          empresa: '',
          servico: '',
          mensagem: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="https://nensolucoes.com.br/nenlogo-escura.png" 
                alt="N&N Soluções Empresariais" 
                className="h-16 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <button
                onClick={() => navigate('/')}
                className="text-gray-700 transition-colors cursor-pointer"
                style={{ color: '#72142B' }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.7')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                Home
              </button>
              <button
                onClick={() => navigate('/servicos')}
                className="text-gray-700 transition-colors cursor-pointer"
                style={{ color: '#72142B' }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.7')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                Serviços
              </button>
              <button
                onClick={() => navigate('/sobre')}
                className="text-gray-700 transition-colors cursor-pointer"
                style={{ color: '#72142B' }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.7')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                Sobre
              </button>
              <button
                onClick={() => navigate('/contato')}
                className="font-semibold cursor-pointer"
                style={{ color: '#72142B' }}
              >
                Contato
              </button>
              <button
                onClick={() => navigate('/agendamento')}
                className="text-gray-700 transition-colors cursor-pointer"
                style={{ color: '#72142B' }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.7')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                Agendamento
              </button>
            </nav>
            <a
              href="https://wa.me/5562993911090"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white px-6 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#72142B' }}
              onMouseEnter={(e) => (e.target.style.opacity = '0.8')}
              onMouseLeave={(e) => (e.target.style.opacity = '1')}
            >
              WhatsApp
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20professional%20office%20reception%20area%20with%20comfortable%20seating%2C%20warm%20welcoming%20atmosphere%2C%20elegant%20business%20environment%20with%20burgundy%20and%20cream%20color%20scheme%2C%20professional%20lighting%20clean%20contemporary%20design%20conveying%20accessibility%20and%20customer%20service%20excellence&width=1920&height=1080&seq=contato-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#72142B]/80 via-[#72142B]/40 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="mb-6">
                <span
                  className="inline-block px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm"
                  style={{ backgroundColor: '#72142B', color: '#FAF7F2' }}
                >
                  ✨ Entre em contato
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Vamos conversar
                <span className="block" style={{ color: '#FAEBEA' }}>
                  sobre seu negócio
                </span>
              </h1>
              <p className="text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed">
                Estamos prontos para ajudar você a simplificar sua contabilidade e
                impulsionar seu crescimento
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: '#72142B' }}
                  >
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-semibold">WhatsApp</div>
                    <div className="text-gray-300">(62) 99391-1090</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: '#72142B' }}
                  >
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-semibold">E-mail</div>
                    <div className="text-gray-300">
                      contato@nnsolucoes.com.br
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: '#72142B' }}
                  >
                    <i className="ri-time-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-semibold">Horário de atendimento</div>
                    <div className="text-gray-300">
                      Segunda a sexta: 8h às 18h
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: '#72142B' }}
                  >
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-semibold">Endereço</div>
                    <div className="text-gray-300">
                      Av. Goiás, 606 - Setor Central, Goiânia - GO
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulário de Contato */}
      <section className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span
              className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4"
              style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}
            >
              Formulário de Contato
            </span>
            <h2
              className="text-4xl lg:text-5xl font-bold mb-6"
              style={{ color: '#72142B' }}
            >
              Envie sua mensagem
            </h2>
            <p className="text-xl text-gray-600">
              Preencha o formulário abaixo e entraremos em contato em até 24
              horas
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <form
              id="contato-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="nome"
                    className="block text-sm font-medium mb-2"
                    style={{ color: '#72142B' }}
                  >
                    Nome completo *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                    placeholder="Seu nome completo"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium mb-2"
                    style={{ color: '#72142B' }}
                  >
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-sm font-medium mb-2"
                    style={{ color: '#72142B' }}
                  >
                    Telefone/WhatsApp *
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div>
                  <label
                    htmlFor="empresa"
                    className="block text-sm font-medium mb-2"
                    style={{ color: '#72142B' }}
                  >
                    Nome da empresa
                  </label>
                  <input
                    type="text"
                    id="empresa"
                    name="empresa"
                    value={formData.empresa}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                    placeholder="Nome da sua empresa"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="servico"
                  className="block text-sm font-medium mb-2"
                  style={{ color: '#72142B' }}
                >
                  Serviço de interesse
                </label>
                <select
                  id="servico"
                  name="servico"
                  value={formData.servico}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Selecione um serviço</option>
                  <option value="contabilidade">Contabilidade Completa</option>
                  <option value="bpo-financeiro">BPO Financeiro</option>
                  <option value="consultoria-empresarial">
                    Consultoria Empresarial
                  </option>
                  <option value="departamento-pessoal">
                    Departamento Pessoal
                  </option>
                  <option value="consultoria-financeira">
                    Consultoria Financeira
                  </option>
                  <option value="declaracao-impostos">
                    Declaração de Impostos
                  </option>
                  <option value="abertura-empresa">Abertura de Empresa</option>
                  <option value="outros">Outros</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="mensagem"
                  className="block text-sm font-medium mb-2"
                  style={{ color: '#72142B' }}
                >
                  Mensagem *
                </label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm resize-none"
                  placeholder="Conte-nos mais sobre sua necessidade..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.mensagem.length}/500 caracteres
                </div>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="text-white px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#72142B' }}
                  onMouseEnter={(e) =>
                    !isSubmitting && (e.target.style.opacity = '0.8')
                  }
                  onMouseLeave={(e) =>
                    !isSubmitting && (e.target.style.opacity = '1')
                  }
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar mensagem'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div
                  className="text-center p-4 rounded-lg"
                  style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}
                >
                  <i className="ri-check-circle-line text-2xl mb-2"></i>
                  <p className="font-semibold">
                    Mensagem enviada com sucesso!
                  </p>
                  <p className="text-sm">
                    Entraremos em contato em até 24 horas.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="text-center p-4 bg-red-100 text-red-700 rounded-lg">
                  <i className="ri-error-warning-line text-2xl mb-2"></i>
                  <p className="font-semibold">Erro ao enviar mensagem</p>
                  <p className="text-sm">
                    Tente novamente ou entre em contato pelo WhatsApp.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Outras formas de contato */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span
              className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4"
              style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}
            >
              Outras formas de contato
            </span>
            <h2
              className="text-4xl lg:text-5xl font-bold mb-6"
              style={{ color: '#72142B' }}
            >
              Escolha como prefere falar conosco
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div
              className="text-center p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              style={{ backgroundColor: '#FAEBEA' }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: '#72142B' }}
              >
                <i className="ri-whatsapp-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>
                WhatsApp
              </h3>
              <p className="text-gray-700 mb-6">
                Atendimento rápido e direto pelo WhatsApp
              </p>
              <a
                href="https://wa.me/5562993911090"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-white px-6 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer font-semibold"
                style={{ backgroundColor: '#72142B' }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                Chamar no WhatsApp
              </a>
            </div>

            <div
              className="text-center p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              style={{ backgroundColor: '#FAEBEA' }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: '#72142B' }}
              >
                <i className="ri-mail-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>
                E-mail
              </h3>
              <p className="text-gray-700 mb-6">
                Envie sua dúvida por e-mail e responderemos em breve
              </p>
              <a
                href="mailto:contato@nnsolucoes.com.br"
                className="inline-block text-white px-6 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer font-semibold"
                style={{ backgroundColor: '#72142B' }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                Enviar e-mail
              </a>
            </div>

            <div
              className="text-center p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              style={{ backgroundColor: '#FAEBEA' }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: '#72142B' }}
              >
                <i className="ri-calendar-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>
                Agendamento
              </h3>
              <p className="text-gray-700 mb-6">
                Agende uma consulta personalizada
              </p>
              <button
                onClick={() => navigate('/agendamento')}
                className="text-white px-6 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer font-semibold"
                style={{ backgroundColor: '#72142B' }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                Agendar consulta
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <img 
                src="https://nensolucoes.com.br/nenlogo-cinca.png" 
                alt="N&N Soluções Empresariais" 
                className="h-16 w-auto mb-4 cursor-pointer"
                onClick={() => navigate('/')}
              />
              <p className="text-gray-300 mb-6">
                Simplificando sua contabilidade para você focar no que realmente importa.
              </p>
              <div className="space-y-2">
                <p className="flex items-center text-gray-300">
                  <i className="ri-phone-line mr-3"></i>
                  WhatsApp: (62) 99391-1090
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-mail-line mr-3"></i>
                  contato@nnsolucoes.com.br
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-map-pin-line mr-3"></i>
                  Av. Goiás, 606 - Setor Central, Goiânia - GO
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Links Úteis</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigate('/servicos')}
                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    Serviços
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigate('/sobre')}
                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    Sobre
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigate('/contato')}
                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    Contato
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigate('/agendamento')}
                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    Agendamento
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Nossos Serviços</h4>
              <ul className="space-y-2">
                <li>Contabilidade Completa</li>
                <li>BPO Financeiro</li>
                <li>Consultoria Empresarial</li>
                <li>Departamento Pessoal</li>
                <li>Consultoria Financeira</li>
                <li>Declaração de Impostos</li>
                <li>Abertura de Empresa</li>
                <li>Outros</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-12 pt-8 text-center">
            <p className="text-gray-300">
              © 2026 N&N Soluções Empresariais. Todos os direitos reservados. |
              <a
                href="https://www.instagram.com/rodriigocoelho"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-100 ml-1 cursor-pointer"
                style={{ color: '#FAEBEA' }}
              >
                RS DIGITAL
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
